/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { selectAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { useAppSelector } from '../../../store';
import { searchSettingsTranslation } from '../../actions/util/sysTranslations';
import { MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES } from '../../ia/util/multiLingualConvoSearch';
import {
  setDraftMaxPrimaryResults,
  setDraftIsConversationalSearchEnabled,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftIsRefinedResults,
  setDraftIsHighlightAnswers,
  setDraftIsDisplayShowMore,
  setSearchSkillDraft,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  updateDraftInstanceProjectIds,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftConversationalSearchEnabledDefault,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setDraftConversationalSearchMultiTurnDefault,
  setDraftConversationalSearchMultiTurn
} from '../store/reducer';
import {
  selectDraftMaxPrimaryResults,
  selectDraftIsConversationalSearchEnabled,
  selectDraftConversationalSearchRetrievalConfidenceThreshold,
  selectDraftConversationalSearchConfidenceThreshold,
  selectDraftConversationalSearchVerbosityOption,
  selectDraftConversationalSearchResponseConfidenceThreshold,
  selectDraftIsRefinedResults,
  selectDraftIsHighlightAnswers,
  selectDraftIsDisplayShowMore,
  selectSelectedInstanceId,
  selectSelectedProjectId,
  selectInstanceById,
  selectDraftSuccessMessage,
  selectDraftNoResultsMessage,
  selectDraftConnectivityMessage,
  selectDraftTitleOptionValue,
  selectDraftBodyOptionValue,
  selectDraftUrlOptionValue,
  selectIsDirty,
  selectIsDraftUninitialized,
  selectDraftSkillId,
  selectDraftDiscoveryInstanceId,
  selectDraftDiscoveryProjectId,
  selectDraftCitationsTitleValue,
  selectDraftMaxCitationsValue,
  selectDraftConversationalSearchMultiTurn
} from '../store/selectors';
import { NUMBER_OF_RESULTS, ASSISTANT_NEW_VERSION } from '../util/constants';
import { messages } from './messages';

export const useDiscoveryDraft = () => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();

  const assistant = useAppSelector(selectAssistant);
  const selectedInstanceId = useSelector(selectSelectedInstanceId);
  const selectedInstance = useSelector(
    state => selectInstanceById(state, selectedInstanceId),
    shallowEqual
  );
  const selectedProjectId = useSelector(selectSelectedProjectId);
  // There is currently a store bug that auto-creates all draft and live
  // skills with the language 'xx'. To get the correct language associated
  // with the skill, we must grab from the assistant
  const language = assistant?.language;

  const setNewDiscoveryDraft = environmentId => {
    if (!selectedInstanceId) {
      throw new Error(
        'setNewDiscoveryDraft requires non-null selectedInstanceId'
      );
    }
    if (!selectedProjectId) {
      throw new Error(
        'setNewDiscoveryDraft requires non-null selectedProjectId'
      );
    }

    let searchSettingsMessages = {
      success: t(messages.successDefault),
      no_result: t(messages.noResultsDefault),
      error: t(messages.connectivityIssueDefault)
    };
    if (MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES.includes(language)) {
      searchSettingsMessages = searchSettingsTranslation[language].messages;
    }
    const url = selectedInstance?.credentials?.url ?? '';
    const draft = {
      name: 'newIA-created',
      type: 'search',
      language: language || 'en',
      assistant_id: environmentId,
      skill_id: undefined,
      search_settings: {
        discovery: {
          instance_id: selectedInstanceId,
          project_id: selectedProjectId,
          passages: false,
          url: url,
          assistant_version: ASSISTANT_NEW_VERSION,
          max_primary_results: NUMBER_OF_RESULTS.DEFAULT,
          max_total_results: NUMBER_OF_RESULTS.SHOW_MORE_MAX,
          confidence_threshold: 0.0,
          highlight: true,
          ...(!isCpd && {
            find_answers: true
          })
        },
        messages: searchSettingsMessages,
        schema_mapping: {
          title: '',
          body: '',
          url: ''
        }
      }
    };
    dispatch(setSearchSkillDraft({ skill: draft, isDirty: true }));
  };

  const initializeAutocreatedSkill = skill => {
    const autocreatedSkill = _.cloneDeep(skill);
    let searchSettingsMessages = {
      success: t(messages.successDefault),
      no_result: t(messages.noResultsDefault),
      error: t(messages.connectivityIssueDefault)
    };
    if (MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES.includes(language)) {
      searchSettingsMessages = searchSettingsTranslation[language].messages;
    }
    autocreatedSkill.language = language || 'en';
    autocreatedSkill.search_settings = {
      discovery: {
        instance_id: selectedInstanceId,
        project_id: selectedProjectId,
        passages: false,
        url: '',
        assistant_version: ASSISTANT_NEW_VERSION,
        max_primary_results: NUMBER_OF_RESULTS.DEFAULT,
        max_total_results: NUMBER_OF_RESULTS.SHOW_MORE_MAX,
        confidence_threshold: 0.0,
        highlight: true,
        ...(!isCpd && {
          find_answers: true
        })
      },
      messages: searchSettingsMessages,
      schema_mapping: {
        title: '',
        body: '',
        url: ''
      }
    };
    dispatch(setSearchSkillDraft({ skill: autocreatedSkill, isDirty: true }));
  };

  const updateInstanceProjectIds = () => {
    const url = selectedInstance?.credentials?.url ?? '';
    const data = {
      language: language || 'en',
      url
    };
    dispatch(updateDraftInstanceProjectIds(data));
  };

  const isDirty = useSelector(selectIsDirty);
  const skillId = useSelector(selectDraftSkillId);
  const discoveryInstanceId = useSelector(selectDraftDiscoveryInstanceId);
  const discoveryProjectId = useSelector(selectDraftDiscoveryProjectId);

  const isNew = !discoveryProjectId || !skillId;

  const isUninitialized = useSelector(selectIsDraftUninitialized);

  const maxPrimaryResults = useSelector(selectDraftMaxPrimaryResults);
  const setMaxPrimaryResults = number => {
    dispatch(setDraftMaxPrimaryResults(number));
  };

  const isRefinedResults = useSelector(selectDraftIsRefinedResults);
  const setIsRefinedResults = value => {
    dispatch(setDraftIsRefinedResults(value));
  };

  const isHighlightAnswers = useSelector(selectDraftIsHighlightAnswers);
  const setIsHighlightAnswers = value => {
    dispatch(setDraftIsHighlightAnswers(value));
  };

  const isDisplayShowMore = useSelector(selectDraftIsDisplayShowMore);
  const setIsDisplayShowMore = value => {
    dispatch(setDraftIsDisplayShowMore(value));
  };

  const successMessage = useSelector(selectDraftSuccessMessage);
  const setSuccessMessage = value => {
    dispatch(setDraftSuccessMessage(value));
  };

  const noResultsMessage = useSelector(selectDraftNoResultsMessage);
  const setNoResultsMessage = value =>
    dispatch(setDraftNoResultsMessage(value));

  const connectivityMessage = useSelector(selectDraftConnectivityMessage);
  const setConnectivityMessage = value =>
    dispatch(setDraftConnectivityMessage(value));

  const titleOptionValue = useSelector(selectDraftTitleOptionValue);
  const setTitleOptionValue = value =>
    dispatch(setDraftTitleOptionValue(value));

  const bodyOptionValue = useSelector(selectDraftBodyOptionValue);
  const setBodyOptionValue = value => dispatch(setDraftBodyOptionValue(value));

  const urlOptionValue = useSelector(selectDraftUrlOptionValue);
  const setUrlOptionValue = value => dispatch(setDraftUrlOptionValue(value));

  const isConversationalSearchEnabled = useSelector(
    selectDraftIsConversationalSearchEnabled
  );
  const conversationalSearchRetrievalConfidenceThreshold = useSelector(
    selectDraftConversationalSearchRetrievalConfidenceThreshold
  );
  const conversationalSearchConfidenceThreshold = useSelector(
    selectDraftConversationalSearchConfidenceThreshold
  );
  const conversationalSearchVerbosityOption = useSelector(
    selectDraftConversationalSearchVerbosityOption
  );
  const conversationalSearchResponseConfidenceThreshold = useSelector(
    selectDraftConversationalSearchResponseConfidenceThreshold
  );
  const setIsConversationalSearchEnabled = value =>
    dispatch(setDraftIsConversationalSearchEnabled(value));
  const setConversationalSearchRetrievalConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchRetrievalConfidenceThreshold(value));
  };
  const setConversationalSearchConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchConfidenceThreshold(value));
  };
  const setConversationalSearchVerbosityOption = value => {
    dispatch(setDraftConversationalSearchVerbosityOption(value));
  };
  const setConversationalSearchResponseConfidenceThreshold = value => {
    dispatch(setDraftConversationalSearchResponseConfidenceThreshold(value));
  };

  const citationsTitleValue = useSelector(selectDraftCitationsTitleValue);
  const setCitationsTitleValue = value =>
    dispatch(setDraftCitationsTitleValue(value));

  const maxCitationsValue = useSelector(selectDraftMaxCitationsValue);
  const setMaxCitationsValue = value =>
    dispatch(setDraftMaxCitationsValue(value));

  const setConversationalSearchEnabledDefault = () =>
    dispatch(setDraftConversationalSearchEnabledDefault());
  const setConversationalSearchRetrievalConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchRetrievalConfidenceThresholdDefault());
  const setConversationalSearchConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchConfidenceThresholdDefault());
  const setConversationalSearchVerbosityOptionDefault = () =>
    dispatch(setDraftConversationalSearchVerbosityOptionDefault());
  const setConversationalSearchResponseConfidenceThresholdDefault = () =>
    dispatch(setDraftConversationalSearchResponseConfidenceThresholdDefault());
  const setCitationsTitleValueDefault = () =>
    dispatch(
      setDraftCitationsTitleValueDefault(t(messages.citationsTitleDefault))
    );
  const setMaxCitationsValueDefault = () =>
    dispatch(setDraftMaxCitationsValueDefault(-1));

  const setConversationalSearchMultiTurnDefault = () =>
    dispatch(setDraftConversationalSearchMultiTurnDefault());
  const setConversationalSearchMultiTurn = value => {
    dispatch(setDraftConversationalSearchMultiTurn(value));
  };
  const isConversationalSearchMultiTurnEnabled = useSelector(
    selectDraftConversationalSearchMultiTurn
  );

  return {
    setNewDiscoveryDraft,
    initializeAutocreatedSkill,
    updateInstanceProjectIds,
    isDirty,
    isNew,
    isUninitialized,
    skillId,
    discoveryInstanceId,
    discoveryProjectId,
    maxPrimaryResults,
    setMaxPrimaryResults,
    isRefinedResults,
    setIsRefinedResults,
    isHighlightAnswers,
    setIsHighlightAnswers,
    isDisplayShowMore,
    setIsDisplayShowMore,
    successMessage,
    setSuccessMessage,
    noResultsMessage,
    setNoResultsMessage,
    connectivityMessage,
    setConnectivityMessage,
    titleOptionValue,
    setTitleOptionValue,
    bodyOptionValue,
    setBodyOptionValue,
    urlOptionValue,
    setUrlOptionValue,
    isConversationalSearchEnabled,
    conversationalSearchRetrievalConfidenceThreshold,
    conversationalSearchConfidenceThreshold,
    conversationalSearchVerbosityOption,
    conversationalSearchResponseConfidenceThreshold,
    setIsConversationalSearchEnabled,
    setConversationalSearchRetrievalConfidenceThreshold,
    setConversationalSearchConfidenceThreshold,
    setConversationalSearchVerbosityOption,
    setConversationalSearchResponseConfidenceThreshold,
    citationsTitleValue,
    setCitationsTitleValue,
    maxCitationsValue,
    setMaxCitationsValue,
    setConversationalSearchEnabledDefault,
    setConversationalSearchRetrievalConfidenceThresholdDefault,
    setConversationalSearchConfidenceThresholdDefault,
    setConversationalSearchVerbosityOptionDefault,
    setConversationalSearchResponseConfidenceThresholdDefault,
    setCitationsTitleValueDefault,
    setMaxCitationsValueDefault,
    setConversationalSearchMultiTurnDefault,
    setConversationalSearchMultiTurn,
    isConversationalSearchMultiTurnEnabled
  };
};
